// src/components/ui/emptyState/buildEmptyState.tsx
import React from 'react';
import Link from 'next/link';

import type { ActionKind, EmptyActionSpec, EmptyPreset } from './emptyStates';
import Button from '@/components/ui/button/submit/Button';

export type EmptyActionBinding = {
  /** 行為：交給頁面決定 */
  href?: string;
  onClick?: () => void;
  disabled?: boolean;

  /** 頁面可覆蓋文案（A/B / 情境） */
  label?: React.ReactNode;

  /** 測試用 */
  'data-testid'?: string;
};

export type EmptyActionMap = Partial<Record<ActionKind, EmptyActionBinding>>;

type Options = {
  actions?: EmptyActionMap;

  /** 頁面想覆蓋 preset 的視覺（tone/文案等） */
  overrides?: Partial<EmptyPreset>;
};

function renderAction(spec: EmptyActionSpec, binding?: EmptyActionBinding): React.ReactNode {
  if (!binding) return null;

  const label = binding.label ?? spec.label;
  const size = spec.ui?.size ?? 'md';
  const color = spec.ui?.color ?? 'brand';
  const width = spec.ui?.width ?? 'fit';

  // ✅ href：用 Link + legacyBehavior + Button(as="a")，避免巢狀 <a>
  if (binding.href) {
    return (
      <Link href={binding.href} legacyBehavior passHref prefetch={false}>
        <Button
          as="a"
          href={binding.href}
          size={size}
          color={binding.disabled ? 'disabled' : color}
          width={width}
          disabled={binding.disabled}
        >
          {label}
        </Button>
      </Link>
    );
  }

  // ✅ onClick：直接用 Button
  return (
    <Button
      size={size}
      color={binding.disabled ? 'disabled' : color}
      width={width}
      onClick={binding.onClick}
      disabled={binding.disabled}
    >
      {label}
    </Button>
  );
}

/**
 * ✅ 一行用法的關鍵：把 preset + 行為綁好，回傳給 <EmptyState {...props} />
 */
export function buildEmptyState(preset: EmptyPreset, options?: Options) {
  const merged: EmptyPreset = { ...preset, ...(options?.overrides ?? {}) };

  const spec = merged.actionSpec;
  const binding = spec ? options?.actions?.[spec.kind] : undefined;

  return {
    ...merged,
    action: spec ? renderAction(spec, binding) : undefined,
  };
}
